import { logToServer } from '@/utils/logger';
import { StateStorage } from 'zustand/middleware';

type StorageValue = string | number | boolean | Record<string, unknown>;

class LocalStorageUtils {
  // Add known persist store keys that can be cleaned up if needed
  private static CLEANABLE_STORES = [
    'review-store',  // Review store can be refetched
    'image-store',   // OLD image data can be refetched
    'image-store-v2', // NEW image data can be refetched
  ];

  private static getUserEmail(): string {
    // Delay import to avoid circular dependency
    let userStore: any;
    try {
      userStore = require('@/stores/user').useUserStore;
    } catch {
      return 'unknown';
    }
    const userStats = userStore.getState().userStats;
    return userStats.google_email || userStats.discord_email || userStats.login_email || 'unknown';
  }

  private static handleQuotaExceededError() {
    const { showToast } = require('@/stores/app');
    const { ToastType } = require('@/types/toast');
    showToast("Your browser cache is full. Please try clearing your cache to continue.", ToastType.ERROR);
  }
  // Estimate localStorage size in bytes
  private static getStorageSize(): number {
    let total = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        total += (localStorage[key].length + key.length) * 2; // UTF-16 uses 2 bytes per char
      }
    }
    return total;
  }

  // Check if we're near quota (80% full)
  private static isStorageNearQuota(): boolean {
    try {
      const quota = 5 * 1024 * 1024; // 5MB typical quota
      const used = this.getStorageSize();
      return used > quota * 0.8;
    } catch (e) {
      console.warn('Storage quota check failed:', e);
      return false;
    }
  }

  // Cleanup least important data
  private static cleanupStorage(): boolean {
    let cleaned = false;
    try {
      const initialSize = this.getStorageSize();
      // First try cleaning review store
      this.removeItem('review-store');
      cleaned = this.getStorageSize() < initialSize; // Check if size actually reduced

      // If still near quota, clean image store
      if (this.isStorageNearQuota()) {
        const currentSize = this.getStorageSize();
        this.removeItem('image-store');
        this.removeItem('image-store-v2');
        // Also consider cleaning the store that's currently causing issues
        // if it's deemed less critical than others during a cleanup
        // this.removeItem('characters-store'); 
        cleaned = cleaned || (this.getStorageSize() < currentSize);
      }

      return cleaned; // Indicate if cleanup potentially helped
    } catch (e) {
      console.error('Storage cleanup failed:', e);
      logToServer('error', `[USER: ${this.getUserEmail()}] Storage cleanup failed (current size: ${this.getStorageSize()}): ${e}`);
      return false;
    }
  }

  static setItem(key: string, value: StorageValue): void {
    if (typeof window === 'undefined') return;

    try {
      const stringValue = JSON.stringify(value);
      localStorage.setItem(key, stringValue);
    } catch (e: any) {
      if (
        e.name === 'QuotaExceededError' ||
        e.code === 22 || // Chrome quota error
        e.code === 1014 // Firefox quota error
      ) {
        // Call cleanup
        const cleanupSuccess = this.cleanupStorage(); 
        if (cleanupSuccess) {
          // Try setting the item again ONLY if cleanup might have helped
          try {
            const stringValue = JSON.stringify(value);
            localStorage.setItem(key, stringValue);
          } catch (retryError: any) {
             // Only log final failure if the retry fails
             console.error('Storage operation failed even after cleanup:', retryError);
             logToServer( 'error', `[USER: ${this.getUserEmail()}] Storage operation failed for key "${key}" even after cleanup: ${retryError}`);
             this.handleQuotaExceededError();
          }
        } else {
           // Log if cleanup didn't help and we didn't retry
           console.error('Storage operation failed, cleanup did not free space:', e);
           logToServer('error', `[USER: ${this.getUserEmail()}] Storage operation failed for key "${key}", cleanup ineffective: ${e}`);
           this.handleQuotaExceededError();
        }
      } else {
        // Catch other potential errors during the initial setItem
        console.error(`Storage operation failed for key "${key}":`, e);
        logToServer('error', `[USER: ${this.getUserEmail()}] Storage operation failed for key "${key}": ${e}`);
        this.handleQuotaExceededError();
      }
    }
  }
  static getItem<T>(key: string): T | null {
    if (typeof window !== 'undefined') {
      try {
        const item = localStorage.getItem(key);
        return item ? (JSON.parse(item) as T) : null;
      } catch (e: any) {
        if (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014) {
          this.handleQuotaExceededError();
        }
        return null;
      }
    }
    return null;
  }
  static removeItem(key: string): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(key);
    }
  }
  static hasItem(key: string): boolean {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(key) !== null;
    }
    return false;
  }
  static clear(): void {
    if (typeof window !== 'undefined') {
      localStorage.clear();
    }
  }

  // --- Add Adapter for Zustand ---
  static zustandStorageAdapter: StateStorage = {
    getItem: (name) => {
      try {
        if (typeof window === 'undefined') return null;

        // Get the value from your utility
        const value = LocalStorageUtils.getItem(name);
        
        if (value === null || value === undefined) {
          return null;
        }
        
        // Check if it's already a string or needs conversion
        if (typeof value === 'string') {
          // Already a string, return as is
          return value;
        } else {
          // It's an object, convert to string
          return JSON.stringify(value);
        }
      } catch (e) {
        return null;
      }
    },
    setItem: (name, value) => {
      if (typeof window === 'undefined') return null;
      const parsedValue = JSON.parse(value); // Parse value from Zustand
      LocalStorageUtils.setItem(name, parsedValue); // Use our setItem logic
    },
    removeItem: (name) => {
      if (typeof window === 'undefined') return;
      LocalStorageUtils.removeItem(name);
    },
  };
  // --- End Adapter ---
}

export default LocalStorageUtils;
